import React, { memo, useCallback, Fragment, useEffect } from 'react';
import { useObservable } from '@core/utils/hooks/rxjs';
import { Label, Row, Col } from 'reactstrap';
import { FormInput } from '@core/components/form';
import { SortableList } from '@modules/common/Sortable';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { FormHTML } from '@core/components/form/FormHTML';
import { SequenceDrillBlockVariation, SequenceDrillWidgetForm, SequenceForm } from './SequenceDrillBlock';
import { SequenceDrillItem } from './SequenceDrillBlockItem';

export interface ScenarioDrillBlockSettingsProps {
  form: SequenceDrillWidgetForm;
  variation: SequenceDrillBlockVariation;
}

export const SequenceDrillBlockSettings = memo((props: ScenarioDrillBlockSettingsProps) => {
  const { form, variation } = props;
  const sequence = useObservable(form.controls.sequence.items$, []);
  const sequenceValues = useObservable(form.controls.sequence.value$, []);

  useEffect(() => {
    if (sequence.length === 0) {
      form.controls.sequence.push();
    }
  }, [form, sequence]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newSequence = R.move(sequenceValues, oldIndex, newIndex);
      form.patchValue({
        sequence: newSequence,
      });
    },
    [form, sequenceValues],
  );

  return (
    <div>
      <Fragment>
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {sequence.map((ctrl, index) => {
            return <SortableSequenceItem key={index} index={index} ctrl={ctrl} form={form} i={index} />;
          })}
        </SortableList>
      </Fragment>
    </div>
  );
});

interface SequenceItemProps {
  i: number;
  ctrl: SequenceForm;
  form: SequenceDrillWidgetForm;
}

const SequenceItem = memo((props: SequenceItemProps) => {
  const { ctrl, form, i } = props;

  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical" style={{ cursor: 'move' }}></i>);

  return (
    <div>
      <Row key={`item-${i} mb-2 mt-2`}>
        <Col md={12}>
          <FormHTML autoFocus minHeight={300} control={ctrl.controls.description} minimal={true} />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <p>
            Note: Add items and set them in correct order. The student will see them in a random sequence and have to set in the order you provide
            here.
          </p>
        </Col>
      </Row>
      <SequenceDrillItem control={ctrl} />
      <hr />
    </div>
  );
});

export const SortableSequenceItem = SortableElement(SequenceItem);
